import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FolderOpen, Plus, FileText, Shirt, ExternalLink, Download, Upload, Trash2, Pencil } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Documents() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [families, setFamilies] = useState([]);

  const load = () => {
    api.get("/resources").then((r) => setItems(r.data));
    if (isDirector) {
      api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
      api.get("/people/dancers").then((r) => setDancers(r.data)).catch(() => {});
      api.get("/people/families").then((r) => setFamilies(r.data)).catch(() => {});
    }
  };
  useEffect(load, []);
  const classMap = Object.fromEntries(classes.map((c) => [c.id, c.name]));
  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, `${d.first_name} ${d.last_name}`]));
  const famMap = Object.fromEntries(families.map((f) => [f.id, f.name]));

  const empty = { kind: "document", title: "", description: "", url: "", file_id: "", file_url: "", file_name: "", scope: "studio", class_id: "", dancer_id: "", family_id: "", size: "", color: "", due_date: "", cost: "", notes: "" };
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const openNew = (kind) => { setForm({ ...empty, kind }); setEditId(null); setFile(null); setDlg(true); };
  const openEdit = (r) => { setForm({ ...empty, ...r }); setEditId(r.id); setFile(null); setDlg(true); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Add a title"); return; }
    try {
      let payload = { ...form };
      if (file) { const fd = new FormData(); fd.append("file", file); const r = await api.post("/files", fd); payload.file_id = r.data.id; payload.file_url = r.data.url; payload.file_name = r.data.name; }
      if (editId) await api.put(`/resources/${editId}`, payload);
      else await api.post("/resources", payload);
      toast.success("Saved"); setDlg(false); setForm(empty); setFile(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (r) => { await api.delete(`/resources/${r.id}`); toast.success("Removed"); load(); };

  const scopeLabel = (r) => r.scope === "studio" ? "Studio-wide"
    : r.scope === "class" ? `Class: ${classMap[r.class_id] || "—"}`
    : r.scope === "dancer" ? `Dancer: ${dancerMap[r.dancer_id] || "—"}`
    : `Family: ${famMap[r.family_id] || "—"}`;
  const fileHref = (r) => r.file_id ? `${BACKEND}/api/files/${r.file_id}` : r.url;

  const Card = ({ r }) => (
    <div data-testid={`resource-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl bg-teal-light flex items-center justify-center shrink-0">
          {r.kind === "costume" ? <Shirt className="w-5 h-5 text-teal-dark" /> : <FileText className="w-5 h-5 text-teal-dark" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-head font-bold">{r.title}</p>
          {isDirector && <Badge variant="outline" className="rounded-full text-[10px] mt-1">{scopeLabel(r)}</Badge>}
        </div>
        {isDirector && <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" data-testid={`edit-resource-${r.id}`} onClick={() => openEdit(r)}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-resource-${r.id}`} onClick={() => remove(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>}
      </div>
      {r.description && <p className="text-sm text-muted-foreground mt-3">{r.description}</p>}
      {r.kind === "costume" && <div className="flex flex-wrap gap-2 mt-3">
        {r.size && <Badge variant="outline" className="rounded-full text-[11px]">Size: {r.size}</Badge>}
        {r.color && <Badge variant="outline" className="rounded-full text-[11px]">{r.color}</Badge>}
        {r.due_date && <Badge className="rounded-full text-[11px] bg-yellow-50 text-yellow-700">Due {r.due_date}</Badge>}
        {r.cost && <Badge className="rounded-full text-[11px] bg-teal-light text-teal-dark">{r.cost}</Badge>}
      </div>}
      {r.notes && <p className="text-xs text-muted-foreground mt-2">{r.notes}</p>}
      {fileHref(r) && <a data-testid={`open-resource-${r.id}`} href={fileHref(r)} target="_blank" rel="noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-dark hover:underline">
        {r.file_id ? <Download className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}{r.file_id ? (r.file_name || "Download") : "Open link"}</a>}
    </div>
  );

  const docs = items.filter((r) => r.kind !== "costume");
  const costumes = items.filter((r) => r.kind === "costume");

  return (
    <div className="stagger-in">
      <PageHeader title="Documents" subtitle="Handbooks, music, forms and costume assignments in one place." testid="documents-page" />
      <Tabs defaultValue="documents">
        <TabsList className="bg-white rounded-full p-1 mb-6">
          <TabsTrigger value="documents" data-testid="docs-tab-documents" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Documents</TabsTrigger>
          <TabsTrigger value="costumes" data-testid="docs-tab-costumes" className="rounded-full data-[state=active]:bg-teal data-[state=active]:text-white">Costumes</TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          {isDirector && <div className="flex justify-end mb-4"><Button data-testid="add-document-button" onClick={() => openNew("document")} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Document</Button></div>}
          {docs.length === 0 ? <EmptyState icon={FolderOpen} title="No documents yet" /> :
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{docs.map((r) => <Card key={r.id} r={r} />)}</div>}
        </TabsContent>
        <TabsContent value="costumes">
          {isDirector && <div className="flex justify-end mb-4"><Button data-testid="add-costume-button" onClick={() => openNew("costume")} className="rounded-full bg-teal hover:bg-teal-dark"><Plus className="w-4 h-4 mr-2" />Add Costume</Button></div>}
          {costumes.length === 0 ? <EmptyState icon={Shirt} title="No costume assignments yet" /> :
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{costumes.map((r) => <Card key={r.id} r={r} />)}</div>}
        </TabsContent>
      </Tabs>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Edit" : "Add"} {form.kind === "costume" ? "Costume" : "Document"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="resource-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="resource-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Share with</Label>
                <select data-testid="resource-scope" className="w-full border rounded-md h-10 px-3" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                  <option value="studio">Whole studio</option><option value="class">A class</option><option value="dancer">A dancer</option><option value="family">A family</option>
                </select></div>
              {form.scope === "class" && <div className="space-y-1.5"><Label>Class</Label><select data-testid="resource-class" className="w-full border rounded-md h-10 px-3" value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}><option value="">Select…</option>{classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>}
              {form.scope === "dancer" && <div className="space-y-1.5"><Label>Dancer</Label><select data-testid="resource-dancer" className="w-full border rounded-md h-10 px-3" value={form.dancer_id} onChange={(e) => setForm({ ...form, dancer_id: e.target.value })}><option value="">Select…</option>{dancers.map((d) => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}</select></div>}
              {form.scope === "family" && <div className="space-y-1.5"><Label>Family</Label><select data-testid="resource-family" className="w-full border rounded-md h-10 px-3" value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })}><option value="">Select…</option>{families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>}
            </div>
            {form.kind === "costume" && <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Size</Label><Input data-testid="costume-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Color</Label><Input data-testid="costume-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Due date</Label><Input data-testid="costume-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cost</Label><Input data-testid="costume-cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="$65" /></div>
            </div>}
            <div className="space-y-1.5"><Label>External link (optional)</Label><Input data-testid="resource-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" /></div>
            <div>
              <input ref={fileRef} data-testid="resource-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
              <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()}><Upload className="w-3.5 h-3.5 mr-1" />{file ? "Change file" : (form.file_name || "Upload file (image/PDF)")}</Button>
              <span className="text-xs text-muted-foreground ml-2">{file?.name}</span>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-resource-button" onClick={save} className="rounded-full bg-teal hover:bg-teal-dark">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
